import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "react-i18next";
import { Package, ImagesSquare, Lightning, ArrowUpRight, WarningCircle, PlugsConnected, CheckCircle, BookOpen } from "@phosphor-icons/react";

export default function Overview() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [stats, setStats] = useState({ products: 0, logos: 0 });
  const [wix, setWix] = useState(null);

  useEffect(() => {
    Promise.all([api.get("/products"), api.get("/logos")]).then(([p, l]) =>
      setStats({ products: p.data.length, logos: l.data.length })).catch(() => {});
    api.get("/wix/dashboard/status").then((r) => setWix(r.data)).catch(() => setWix({ linked: false }));
  }, []);

  const sub = user?.subscription || {};
  const active = sub.status === "active";
  const cards = [
    { icon: Package, label: t("overview.cardProducts"), value: stats.products, to: "/dashboard/products" },
    { icon: ImagesSquare, label: t("overview.cardLogos"), value: stats.logos, to: "/dashboard/logos" },
    { icon: Lightning, label: t("overview.cardRenders"), value: user?.usage?.renders || 0, to: "/dashboard/billing" },
  ];

  return (
    <div className="p-8 max-w-6xl">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.2em] text-gold">{t("overview.backoffice")}</div>
        <h1 className="text-3xl font-bold tracking-tight mt-1">{t("overview.hello", { name: user?.name })}</h1>
        <p className="text-neutral-400 mt-1">{user?.business_name || t("overview.yourStore")} · {t("overview.planPrefix")} {active ? sub.plan : t("overview.noSub")}</p>
      </div>

      {!active && (
        <Link to="/dashboard/billing" data-testid="sub-banner" className="flex items-center gap-3 bg-gold/10 border border-gold/30 rounded-xl p-4 mb-8 hover:bg-gold/15 transition-colors">
          <WarningCircle size={22} className="text-gold" />
          <div className="flex-1 text-sm"><span className="text-gold font-semibold">{t("overview.activateSub")}</span> {t("overview.activateSubRest")}</div>
          <ArrowUpRight className="text-gold" />
        </Link>
      )}

      <a href="/guia" target="_blank" rel="noreferrer" data-testid="overview-guide-banner" className="flex items-center gap-3 border border-gold/30 bg-gradient-to-r from-gold/10 to-transparent rounded-xl p-4 mb-8 hover:border-gold/60 hover:-translate-y-0.5 transition-all">
        <BookOpen size={22} weight="duotone" className="text-gold" />
        <div className="flex-1 text-sm">
          <div className="font-semibold text-neutral-100">Guía de inicio</div>
          <p className="text-neutral-400 mt-0.5">Pasos para dejar tu tienda lista: configurar, conectar tu Gmail, añadir productos y vender.</p>
        </div>
        <ArrowUpRight className="text-gold" />
      </a>

      <div className="grid sm:grid-cols-3 gap-4 mb-8">
        {cards.map((c, i) => (
          <Link key={i} to={c.to} className="border border-white/10 rounded-xl p-5 hover:border-white/25 hover:-translate-y-0.5 transition-all bg-[#121212]">
            <c.icon size={22} weight="duotone" className="text-gold mb-4" />
            <div className="text-3xl font-bold tracking-tight">{c.value}</div>
            <div className="text-sm text-neutral-400">{c.label}</div>
          </Link>
        ))}
      </div>

      {wix && (
        <Link to="/dashboard/integrations" data-testid="overview-wix-panel" className="flex items-center gap-3 border border-white/10 rounded-xl p-5 mb-8 bg-[#121212] hover:border-white/25 transition-all">
          <PlugsConnected size={22} weight="duotone" className={wix.linked ? "text-emerald-400" : "text-neutral-500"} />
          {wix.linked ? (
            <div className="flex-1 text-sm">
              <div className="flex items-center gap-1 font-semibold text-emerald-400"><CheckCircle weight="fill" size={15} /> Tienda Wix conectada</div>
              <p className="text-neutral-400 mt-0.5">{wix.site_url || wix.site_id} · <b className="text-neutral-200" data-testid="overview-wix-synced">{wix.synced_products ?? 0}</b> productos sincronizados al catálogo de Wix</p>
            </div>
          ) : (
            <div className="flex-1 text-sm">
              <div className="font-semibold text-neutral-200">Wix no conectado</div>
              <p className="text-neutral-400 mt-0.5">Abre este panel desde tu sitio Wix (Apps → Hephy 3D) para conectar tu tienda.</p>
            </div>
          )}
          <ArrowUpRight className="text-neutral-400" />
        </Link>
      )}

      <div className="border border-white/10 rounded-xl p-6 bg-[#121212]">
        <h3 className="font-semibold mb-2">{t("overview.startHere")}</h3>
        <ol className="text-sm text-neutral-400 space-y-2 list-decimal list-inside">
          <li>{t("overview.step1")}</li>
          <li>{t("overview.step2")}</li>
          <li>{t("overview.step3")}</li>
          <li>{t("overview.step4")}</li>
        </ol>
        <Link to="/dashboard/products" className="inline-block mt-4 text-gold text-sm hover:underline">{t("overview.goProducts")}</Link>
      </div>
    </div>
  );
}
